import { type UseQueryResult, useQuery, useQueryClient } from 'react-query';
import { isEqual, isArray } from 'lodash';

import type { IMessage, IMessageAttachment } from 'src/services/api/messages/types';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_MESSAGES_PATH } from 'src/services/api/messages/constants';

type IGetLeadMessagesResponse = {
    messages: IMessage[];
}

export type IGetLeadMessagesWithAttachmentsResponse = IGetLeadMessagesResponse & {
    attachments?: IMessageAttachment[];
}

type IGetLeadMessagesOptions ={
    isArchived?: boolean;
    refetchInterval?: number;
    include_attachments?: boolean;
}

export function useLeadMessages(
    leadUuid: string, options: IGetLeadMessagesOptions & { include_attachments: true }
): UseQueryResult<IGetLeadMessagesWithAttachmentsResponse>;
export function useLeadMessages(
    leadUuid: string, options?: IGetLeadMessagesOptions
): UseQueryResult<IMessage[]>;

export function useLeadMessages(
    leadUuid: string,
    options?: IGetLeadMessagesOptions,
) {
    const queryClient = useQueryClient();

    return useQuery(
        [queryKeys.messages, leadUuid],
        async () => {
            const searchParams = new URLSearchParams();
            if (options?.isArchived != null) {
                searchParams.append('is_archived', options.isArchived.toString());
            }
            if (options?.include_attachments) {
                searchParams.append('include_attachments', 'true');
            }

            searchParams.append('lead_uuid', leadUuid);
            const res = await apiClient(GET_MESSAGES_PATH, { searchParams });

            const existing = queryClient.getQueryData<IGetLeadMessagesWithAttachmentsResponse | IMessage[]>(
                [queryKeys.messages, leadUuid]
            );
            const existingMessages = existing ?
                (isArray(existing) ? existing : existing.messages) :
                [];
            const existingByUuid = new Map(existingMessages.map(m => [m.uuid, m]));
            const mergeTranslation = (msg: IMessage): IMessage => {
                const prev = existingByUuid.get(msg.uuid);
                if (prev?.translated_contents && !msg.translated_contents) {
                    return { ...msg, translated_contents: prev.translated_contents };
                }
                return msg;
            };

            if (options?.include_attachments) {
                const data = await res.json<IGetLeadMessagesWithAttachmentsResponse>();
                return { ...data, messages: data.messages.map(mergeTranslation) };
            } else {
                const messages = (await res.json<IGetLeadMessagesResponse>()).messages;
                return messages.map(mergeTranslation);
            }
        }, {
            enabled: !!leadUuid,
            refetchInterval: options?.refetchInterval,
            isDataEqual(prevData: IGetLeadMessagesWithAttachmentsResponse | IMessage[] | undefined, newData: IGetLeadMessagesWithAttachmentsResponse | IMessage[]) {
                if (!prevData) {
                    return false;
                }
                if (isArray(prevData) && isArray(newData)) {
                    return isEqual(prevData, newData);
                }
                // Compare only ids for attachments because presigned url are always different
                return isEqual(
                    (prevData as IGetLeadMessagesWithAttachmentsResponse).messages,
                    (newData as IGetLeadMessagesWithAttachmentsResponse).messages
                ) && isEqual(
                    (prevData as IGetLeadMessagesWithAttachmentsResponse).attachments?.map(({ id }) => id),
                    (newData as IGetLeadMessagesWithAttachmentsResponse).attachments?.map(({ id }) => id)
                );
            }
        },
    );
}
